import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as apiWarehouse from 'api/apiWarehouses';
import * as apiProductSet from 'api/apiProductSets';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useNeedPay } from 'modules/admin';
import type { WarehouseInfo } from '@econtractor/types';

export type OffTransferShow = boolean | number | 'all';

interface UseOffTransferParams {
    wId: number;
    show: OffTransferShow;
    setShow: (show: OffTransferShow) => void;
    fetchStocks: (force?: boolean) => Promise<unknown> | unknown;
    reservObjectId?: number | null;
    fixedToWarehouseId?: number | null;
}

const useOffTransfer = ({
    wId,
    show,
    setShow,
    fetchStocks,
    reservObjectId = null,
    fixedToWarehouseId = null,
}: UseOffTransferParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const { showToast } = useToast();
    const { needPay } = useNeedPay();

    const [addProduct, setAddProduct] = useState<any>(null);
    const [addSet, setAddSet] = useState<any>(null);
    const [addSourceType, setAddSourceType] = useState<'product' | 'set'>('product');
    const [setCount, setSetCount] = useState<number | string>('');
    const [setDescription, setSetDescription] = useState('');
    const [setAddProcess, setSetAddProcess] = useState(false);
    const [aTransfer, setATransfer] = useState<any[]>([]);
    const [transferProcess, setTransferProcess] = useState(false);
    const [transferAllLoading, setTransferAllLoading] = useState(false);
    const [toWarehouse, setToWarehouse] = useState<number | null>(null);
    const isManager = auth?.user?.access?.role === 'manager';
    const splitByPrice = isManager && !params.ignoreWarehousePrice;
    const isTransferAll = show === 'all';

    const reservTarget = useMemo(() => {
        if (!reservObjectId) return null;
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        const objectWarehouse = warehouses.find(
            (wh) => Number(wh.objectId) === Number(reservObjectId) && !Number(wh.reservObjectId),
        );
        if (!objectWarehouse) return { objectWarehouseId: null, objectTeamId: null };
        return {
            objectWarehouseId: objectWarehouse.ID,
            objectTeamId: objectWarehouse.objectTeamId || null,
        };
    }, [reservObjectId, localSettings?.WAREHOUSES]);

    const pushManagerTransferRows = (
        setId: number,
        productId: number,
        productName: string,
        vendorName: string,
        article: string,
        count: number,
        description: string,
        stockRows: any[],
    ) => {
        let remain = count;
        const rows: any[] = [];

        for (const stockRow of stockRows) {
            if (remain <= 0) break;
            const stockCount = Number(stockRow?.stock || 0);
            if (stockCount <= 0) continue;

            const cCount = Math.min(remain, stockCount);
            remain -= cCount;

            rows.push({
                setId,
                product: {
                    ID: productId,
                    value: productId,
                    label: productName,
                    vendor: vendorName || '',
                    article: article || '',
                },
                coming: {
                    cCount,
                    cDescription: description || '',
                    price: Number(stockRow?.operPrice || 0),
                    priceSale: Number(stockRow?.operPriceSale || 0),
                    priceRate: Number(stockRow?.operPriceRate || 0),
                    priceSaleRate: Number(stockRow?.operPriceSaleRate || 0),
                },
            });
        }

        return remain > 0 ? null : rows;
    };

    const buildTransferAllRows = async () => {
        setTransferAllLoading(true);
        try {
            const resStocks = await apiWarehouse.getStocks(wId, {
                page: 1,
                onPage: 999999,
                showAll: false,
            });
            const stocks = (resStocks?.stocks || []).filter(
                (stock: any) => Number(stock?.stockCount || 0) > 0,
            );

            const transferRows: any[] = [];

            for (const stock of stocks) {
                const productId = Number(stock.productId || 0);
                const stockCount = Number(stock.stockCount || 0);
                if (!productId || stockCount <= 0) continue;

                const product = {
                    ID: productId,
                    value: productId,
                    label: stock.prodName || `#${productId}`,
                    vendor: stock.vendorName || '',
                    article: stock.prodArticle || '',
                };

                if (splitByPrice) {
                    const stockRows = (await apiWarehouse.getStockByProductId(wId, productId)) || [];
                    const totalByPrice = stockRows.reduce(
                        (sum: number, row: any) => sum + Number(row?.stock || 0),
                        0,
                    );
                    if (totalByPrice <= 0) continue;
                    const managerRows = pushManagerTransferRows(
                        0,
                        productId,
                        product.label,
                        product.vendor,
                        product.article,
                        totalByPrice,
                        '',
                        stockRows,
                    );
                    if (managerRows?.length) transferRows.push(...managerRows);
                    continue;
                }

                transferRows.push({
                    setId: 0,
                    product,
                    coming: {
                        cCount: stockCount,
                        cDescription: '',
                        price: 0,
                        priceSale: 0,
                        priceRate: 0,
                        priceSaleRate: 0,
                    },
                });
            }

            setATransfer(transferRows);
            if (!transferRows.length) {
                showToast(t('messNoProductsStock'), 'warning');
            }
        } finally {
            setTransferAllLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        setAddSourceType('product');
        setAddProduct(null);
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
        setATransfer([]);
        setToWarehouse(
            fixedToWarehouseId
                ?? reservTarget?.objectWarehouseId
                ?? null,
        );

        if (show === 'all') {
            void buildTransferAllRows();
        }
    }, [show, fixedToWarehouseId, reservTarget?.objectWarehouseId]);

    const handleAddSetTransfer = async () => {
        if (setAddProcess) return;
        const qty = Number(setCount);
        if (!addSet?.value) return;
        if (!Number.isFinite(qty) || qty <= 0) {
            showToast(t('messInputCount'), 'danger');
            return;
        }

        setSetAddProcess(true);
        try {
            const resSet = await apiProductSet.getProductSet(addSet.value);
            if (!resSet?.items?.length) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            const transferRows: any[] = [];

            for (const item of resSet.items) {
                const productId = Number(item.productId || 0);
                const requiredCount = Number(item.itemCount || 0) * qty;
                const productName = item.prodName || `#${productId}`;
                const vendorName = item.vendorName || '';
                const article = item.prodArticle || '';

                if (requiredCount <= 0) continue;

                const stockRows = (await apiWarehouse.getStockByProductId(wId, productId)) || [];
                const totalStock = stockRows.reduce((sum: number, row: any) => sum + Number(row?.stock || 0), 0);
                if (totalStock < requiredCount) {
                    showToast(`${t('messNoProductsStock')}: ${productName}`, 'danger');
                    return;
                }

                if (splitByPrice) {
                    const managerRows = pushManagerTransferRows(
                        Number(resSet.ID || addSet.value || 0),
                        productId,
                        productName,
                        vendorName,
                        article,
                        requiredCount,
                        setDescription,
                        stockRows,
                    );
                    if (!managerRows?.length) {
                        showToast(`${t('messNoProductsStock')}: ${productName}`, 'danger');
                        return;
                    }
                    transferRows.push(...managerRows);
                    continue;
                }

                transferRows.push({
                    setId: Number(resSet.ID || addSet.value || 0),
                    product: {
                        ID: productId,
                        value: productId,
                        label: productName,
                        vendor: vendorName,
                        article,
                    },
                    coming: {
                        cCount: requiredCount,
                        cDescription: setDescription || '',
                        price: 0,
                        priceSale: 0,
                        priceRate: 0,
                        priceSaleRate: 0,
                    },
                });
            }

            if (!transferRows.length) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            setATransfer((prev) => [...prev, ...transferRows]);
            setAddSet(null);
            setSetCount('');
            setSetDescription('');
        } finally {
            setSetAddProcess(false);
        }
    };

    const handleSelectType = (type: 'product' | 'set') => {
        setAddSourceType(type);
        setAddProduct(null);
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
    };

    const handleCancelSetTransfer = () => {
        setAddSet(null);
        setSetCount('');
        setSetDescription('');
    };

    const handleHide = () => {
        setShow(false);
        setTimeout(() => {
            setAddSourceType('product');
            setAddProduct(null);
            setAddSet(null);
            setSetCount('');
            setSetDescription('');
            setSetAddProcess(false);
            setTransferAllLoading(false);
            setToWarehouse(null);
            setATransfer([]);
        }, 300);
    };

    const handleClickApproved = async () => {
        if (needPay()) return;
        if (!toWarehouse) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        setTransferProcess(true);
        const resTransfer = await apiWarehouse.transferBetweenWarehouses(wId, toWarehouse, aTransfer);

        if (resTransfer?.stat) {
            showToast(t('messTransferOk'), 'success');
            handleHide();
            await Promise.resolve(fetchStocks(true));
        } else {
            switch (resTransfer?.errCode) {
                case 'NO_PRODUCT_STOCK':
                    showToast(t('messNoProductsStock'), 'danger');
                    break;
                case 'WAREHOUSE_CLOSED':
                    showToast(t('messWarehouseClosed'), 'danger');
                    break;
                case 'TEAM_DELETED':
                    showToast(t('messTeamDeleted'), 'danger');
                    break;
                case 'CUSTOM_DELETED':
                    showToast(t('messCustomDeleted'), 'danger');
                    break;
                default:
                    showToast(t('common:messError'), 'danger');
            }
        }

        setTransferProcess(false);
    };

    return {
        auth,
        isManager,
        addProduct,
        addSet,
        addSourceType,
        setCount,
        setDescription,
        setAddProcess,
        aTransfer,
        transferProcess,
        transferAllLoading,
        isTransferAll,
        toWarehouse,
        reservObjectId,
        reservTarget,
        setAddProduct,
        setAddSet,
        setSetCount,
        setSetDescription,
        setATransfer,
        setToWarehouse,
        handleAddSetTransfer,
        handleSelectType,
        handleCancelSetTransfer,
        handleClickApproved,
        handleHide,
    };
};

export default useOffTransfer;
